import React, { useEffect, useState } from "react";
import { useLanguage } from "../../../../Authentication/LanguageContext";
import ServerUrl from "../../../../../BACKEND";
import "./Hobbies.css";

interface HobbyItem {
  id: number;
  german: string;
  sentence: string;
  audio: string | null;
  image: string | null;
  tamil: string;
  english: string;
  sinhala: string;
  hindi: string;
  arabic: string;
  turkish: string;
  russian: string;
  chinese: string;
  french: string;
  spanish: string;
  [key: string]: string | number | null;
}

const capitalize = (text: string) =>
  text ? text.charAt(0).toUpperCase() + text.slice(1) : "";

const Hobbies: React.FC = () => {
  const [data, setData] = useState<HobbyItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentlyPlaying, setCurrentlyPlaying] = useState<string | null>(null);
  const { language } = useLanguage();

  useEffect(() => {
    fetch(`${ServerUrl}/api/hobbies`)
      .then((res) => res.json())
      .then((json) => setData(json))
      .catch((err) => console.error("Error fetching hobbies:", err))
      .finally(() => setLoading(false));
  }, [language]);

  const getTranslation = (item: HobbyItem) => {
    const map: Record<string, string> = {
      en: "english",
      de: "german",
      ta: "tamil",
      si: "sinhala",
      hi: "hindi",
      ar: "arabic",
      tr: "turkish",
      ru: "russian",
      zh: "chinese",
      fr: "french",
      es: "spanish",
    };
    return capitalize(String(item[map[language] || "english"] || ""));
  };

  const playAudioOrTTS = (item: HobbyItem) => {
    if (currentlyPlaying === item.german) {
      setCurrentlyPlaying(null);
      return;
    }

    setCurrentlyPlaying(item.german);

    if (item.audio) {
      const audio = new Audio(`${ServerUrl}/storage/${item.audio}`);
      audio.play();
      audio.onended = () => setCurrentlyPlaying(null);
      audio.onerror = () => {
        speak(item.german);
      };
    } else {
      speak(item.german);
    }
  };

  const speak = (text: string) => {
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = "de-DE";
    utterance.onend = () => setCurrentlyPlaying(null);
    speechSynthesis.speak(utterance);
  };

  const getImageUrl = (image: string | null) => {
    if (!image) return `${ServerUrl}/storage/A1Image/placeholder.png`;
    if (image.startsWith("http")) return image;
    return `${ServerUrl}/storage/${image}`;
  };

  const titleMap = {
    en: "Hobbies",
    de: "Hobbys",
    ta: "வேலைகள்",
    si: "සිත්තැටි",
    hi: "शौक",
    ar: "هوايات",
    tr: "Hobiler",
    ru: "Хобби",
    zh: "爱好",
    fr: "Loisirs",
    es: "Pasatiempos",
  };

  const subtitleMap = {
    en: "Hobbies in German",
    de: "Hobbys auf Deutsch",
    ta: "ஜெர்மனில் பொழுதுபோக்குகள்",
    si: "ජර්මන් භාෂාවේ විනෝදාස්වාද",
    hi: "जर्मन में शौक",
    ar: "الهوايات بالألمانية",
    tr: "Almanca'da Hobiler",
    ru: "Хобби на немецком",
    zh: "德语爱好",
    fr: "Loisirs en allemand",
    es: "Pasatiempos en alemán",
  };

  return (
    <div className="hobbies-page-container">
      <div className="hobbies-page-header">
        <div>
          <h1 className="hobbies-page-title">{titleMap["de"]}</h1>
          <p className="hobbies-page-subtitle">{subtitleMap[language]}</p>
        </div>
      </div>

      {loading ? (
        <div className="hobbies-page-loading">Loading...</div>
      ) : (
        <div className="hobbies-page-grid">
          {data.map((item) => {
            const isPlaying = currentlyPlaying === item.german;
            return (
              <div
                key={item.id}
                className={`hobbies-page-card ${
                  isPlaying ? "hobbies-page-playing" : ""
                }`}
                onClick={() => playAudioOrTTS(item)}
              >
                <div className="hobbies-page-image-wrapper">
                  <img
                    src={getImageUrl(item.image)}
                    alt={item.german}
                    className="hobbies-page-image"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = `${ServerUrl}/storage/A1Image/placeholder.png`;
                    }}
                  />
                </div>
                <div className="hobbies-page-german-text">
                  {capitalize(item.german)}
                </div>
                <div className="hobbies-page-translation-text">
                  {getTranslation(item)}
                </div>
                <div className="hobbies-page-sentence-text">{item.sentence}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Hobbies;
